import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import { TournamentPanelLayout } from '../../../layouts/mainMenu/tournament/TournamentPanelLayout';
import type { Tournament } from '../../app/tournaments/types';
import type { LeaderboardEntry, PlatformUser } from '../../app/platform/Platform';
import { TournamentEntryView } from './TournamentEntryView';
import { LoadingSpinnerView } from '../../views/LoadingSpinnerView';
import { MS_IN_DAY, MS_IN_HOUR, MS_IN_MINUTE } from '../../utils/time-utils';
import { getLocalized } from '../../libs/layouts/initPopulate';
import { centerElements } from '../../game/utils/centerElements';

interface TournamentPanelViewConfig {
  currentUser: PlatformUser;
  tournament: Tournament;
  isTournamentActive: boolean;
}

export class TournamentPanelView extends Container {
  public readonly onJoinClicked = new Signal<(tournamentId: string) => void>();

  private readonly layout = new TournamentPanelLayout();

  constructor(private readonly config: TournamentPanelViewConfig) {
    super();
    void this.init();
  }

  private async init(): Promise<void> {
    const { tournament, isTournamentActive } = this.config;
    const { root, wrapper, tournamentTitle, joinButton, spinnerSlot } = this.layout;

    root.props({
      parent: this,
    });

    wrapper.interactiveChildren = false;
    // Always localize from the key. tournament.title may hold a value persisted in
    // localStorage (possibly already-translated), and re-localizing a non-key produces
    // a "<lang :: value>" error string, so we localize the stable 'Tournament' key.
    tournamentTitle.text = getLocalized('Tournament');

    if (isTournamentActive) {
      joinButton.eventMode = 'none';
      joinButton.alpha = 0.3;
    } else {
      joinButton.on('pointertap', () => {
        this.onJoinClicked.emit(tournament.tournamentId);
      });
    }

    this.setEndDate(tournament);

    const leaderboard = await tournament.getLeaderboard();

    new LoadingSpinnerView().props({
      parent: spinnerSlot,
    });

    if (this.destroyed) return;

    spinnerSlot.visible = false;
    this.fillLeaderboard(leaderboard);
  }

  private setEndDate(tournament: Tournament): void {
    const { endsText, endsTimeText } = this.layout;

    const timeLeft = tournament.endsAt - Date.now();

    const days = Math.floor(timeLeft / MS_IN_DAY);
    const hours = Math.floor(timeLeft / MS_IN_HOUR);

    if (days > 0) {
      endsTimeText.text = getLocalized('<amount> days').replace('<amount>', `${days}`);
    } else if (hours > 0) {
      endsTimeText.text = getLocalized('<amount> hours').replace('<amount>', `${hours}`);
    } else {
      endsTimeText.text = getLocalized('<amount> minutes').replace('<amount>', `${Math.ceil(timeLeft / MS_IN_MINUTE)}`);
    }

    centerElements([endsText, endsTimeText], 10);
  }

  private fillLeaderboard(leaderboard: LeaderboardEntry[]): void {
    const { entrySlot } = this.layout;

    const leaderboardToDisplay = this.prepareLeaderboardToDisplay(leaderboard);

    entrySlot.forEach((slot, index) => {
      new TournamentEntryView().setEntry(leaderboardToDisplay[index] ?? null).props({
        parent: slot,
      });
    });
  }

  private prepareLeaderboardToDisplay(leaderboard: LeaderboardEntry[]): (LeaderboardEntry | null)[] {
    const { entrySlot } = this.layout;
    const slotsAmount = entrySlot.length;
    const {
      currentUser: { avatarUrl, id },
    } = this.config;

    const playerEntry = leaderboard.find((entry) => entry.player.currentUser) ?? {
      player: { name: getLocalized('You'), currentUser: true, avatarUrl, id },
      rank: -1,
      score: -1,
    };

    const playerIndex = leaderboard.indexOf(playerEntry);

    if (playerIndex !== -1 && playerIndex < slotsAmount) {
      return leaderboard.slice(0, slotsAmount);
    }

    return [...new Array(slotsAmount - 1).fill(null).map((_, index) => leaderboard[index] ?? null), playerEntry];
  }
}
